import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { comboBoxOptionsOf, emptySelectOption, selectOption } from '@/components/ui/multi-select-utils';
import { coachesTypeaheadSearch } from '@/lib/api-call-hooks';
import { getAdminSession, getAdminSessions, updateSessionOriginalTeacher, updateSessionTeacher } from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  getOriginalTeacherIssueDropdownText,
  getOriginalTeacherIssueOptionClassName,
  getOriginalTeacherIssueOptionHint,
  orderedOriginalTeacherIssueOptions,
  shouldShowReasonField,
} from '@/lib/original-teacher-issue-utils';
import {
  buildUpdateSessionTeacherPayload,
  canEditSessionTeacher,
  getSessionTeacherAssignmentState,
} from '@/lib/coach-assignment-utils';
import { stringIfNull } from '@/lib/string-utils';
import { SessionTeacherDisplay } from '@/routes/_layout/sessions/-components/session-teacher-display';
import { getSessionSubstitutionRequestSummary } from '@airreading/common';
import {
  AdminSession,
  CoachStatus,
  originalTeacherIssueSelectableSchema,
  SubstitutionRequestStatus,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, UserPenIcon } from 'lucide-react';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  sessionTeacher: selectOption.refine((option) => !!option.value, 'Please select a session teacher'),
  shouldUpdateOriginalTeacher: z.boolean().optional(),
  shouldSendConfirmationEmail: z.boolean().optional(),
  originalTeacherClassIssue: originalTeacherIssueSelectableSchema,
  reason: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

const EDIT_SESSION_TEACHER_MODAL = 'session-edit-teacher-modal';

function getSessionTeacherDisplayName(session: AdminSession): string {
  const { mostRecentRequest } = getSessionSubstitutionRequestSummary(session);
  if (mostRecentRequest?.status === SubstitutionRequestStatus.pending) {
    return 'Pending substitute teacher';
  }

  const coachToShow = session.substituteCoach ?? session.coach;
  return coachToShow?.fullNameOrEmail ?? '-';
}

function getOriginalTeacherDisplayName(session: AdminSession): string {
  return session.coach?.fullNameOrEmail ?? '-';
}

function getPendingRequestRequestedByName(session: AdminSession): string | null {
  const { mostRecentRequest } = getSessionSubstitutionRequestSummary(session);
  if (mostRecentRequest?.status !== SubstitutionRequestStatus.pending) {
    return null;
  }
  return mostRecentRequest.requestedByCoach.fullNameOrEmail ?? 'Unknown teacher';
}

export function EditSessionTeacherModal({
  session,
  triggerTestId,
  compactIcon = false,
}: {
  session: AdminSession;
  triggerTestId?: string;
  /** Inline-sized trigger for use next to form labels (avoids h-10 icon button stretching the row). */
  compactIcon?: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutateAsync: updateSessionTeacherMutate } = useMutation({ mutationFn: updateSessionTeacher });
  const { mutateAsync: updateSessionOriginalTeacherMutate } = useMutation({
    mutationFn: updateSessionOriginalTeacher,
  });
  const coachTypeahead = coachesTypeaheadSearch(queryClient, { filters: { statuses: [CoachStatus.ready_to_teach] } });
  const assignmentState = getSessionTeacherAssignmentState(session);
  const sessionTeacherDisplayName = getSessionTeacherDisplayName(session);
  const originalTeacherDisplayName = getOriginalTeacherDisplayName(session);
  const pendingRequestRequestedByName = getPendingRequestRequestedByName(session);

  const defaultValues: DefaultValues<FormData> = {
    sessionTeacher: emptySelectOption,
    shouldUpdateOriginalTeacher: false,
    shouldSendConfirmationEmail: false,
    originalTeacherClassIssue: stringIfNull(session.originalTeacherIssue),
    reason: session.originalTeacherIssueReason ?? '',
  };

  const onSubmit = async ({ formData, closeModal }: SubmitProps<FormData>) => {
    const shouldUpdateOriginalTeacher = formData.shouldUpdateOriginalTeacher ?? false;

    try {
      if (shouldUpdateOriginalTeacher) {
        await updateSessionOriginalTeacherMutate({
          sessionId: session.id,
          payload: { coachId: formData.sessionTeacher.value },
        });

        toast({
          description: (
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CircleCheck className="h-4 w-4 shrink-0 text-green-600" aria-hidden />
              The original teacher and the session teacher have been successfully updated.
            </p>
          ),
        });
      } else {
        const payload = buildUpdateSessionTeacherPayload(session, {
          sessionTeacherId: formData.sessionTeacher.value,
          shouldSendConfirmationEmail: formData.shouldSendConfirmationEmail,
          originalTeacherClassIssue: formData.originalTeacherClassIssue,
          reason: formData.reason,
        });
        await updateSessionTeacherMutate({ sessionId: session.id, payload });

        toast({
          description: (
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CircleCheck className="h-4 w-4 shrink-0 text-green-600" aria-hidden />
              Session teacher is updated
            </p>
          ),
        });
      }

      closeModal();
      queryClient.invalidateQueries(getAdminSessions());
      void queryClient.invalidateQueries(getAdminSession(session.id));
    } catch (error) {
      const errorMessage =
        airServerErrorFrom(error)?.userFacingMessage ??
        (error instanceof Error
          ? error.message
          : shouldUpdateOriginalTeacher
            ? 'There was an error updating the original teacher. Please try again.'
            : 'There was an error updating the session teacher. Please try again.');

      toast({
        title: shouldUpdateOriginalTeacher ? 'Failed to update original teacher' : 'Failed to update session teacher',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Edit session teacher"
      formSchema={formSchema}
      onSubmit={onSubmit}
      confirmBtnLabel="Save"
      cancelBtnLabel="Cancel"
      defaultValues={defaultValues}
      resetOnDefaultValuesChange
      dataTestId={EDIT_SESSION_TEACHER_MODAL}
      trigger={
        <Button
          variant="ghost"
          size="icon"
          className={compactIcon ? 'h-3.5 w-3.5 shrink-0' : undefined}
          data-testid={triggerTestId}
          aria-label="Edit session teacher"
        >
          <UserPenIcon size={compactIcon ? 14 : 16} />
        </Button>
      }
    >
      {({ form }) => {
        const sessionTeacher = form.watch('sessionTeacher');
        const shouldUpdateOriginalTeacher = form.watch('shouldUpdateOriginalTeacher') ?? false;
        const showConfirmationEmail = !!sessionTeacher?.value && !shouldUpdateOriginalTeacher;
        const watchedOriginalTeacherIssue = form.watch('originalTeacherClassIssue');
        const showOriginalTeacherIssueReason = shouldShowReasonField(watchedOriginalTeacherIssue);

        return (
          <div className="mt-4 space-y-4">
            <div className="flex flex-col gap-2">
              <Badge
                variant="secondary"
                className="w-fit max-w-full whitespace-normal text-left font-normal"
                data-testid={`${EDIT_SESSION_TEACHER_MODAL}-current-session-teacher`}
              >
                Current session teacher: {sessionTeacherDisplayName}
              </Badge>

              {assignmentState === 'pending_assignment' && pendingRequestRequestedByName ? (
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>
                    A sub request is currently open for this session. It was requested by{' '}
                    {pendingRequestRequestedByName} and is waiting for coverage.
                  </p>
                </div>
              ) : null}

              <Badge
                variant="secondary"
                className="w-fit max-w-full whitespace-normal text-left font-normal"
                data-testid={`${EDIT_SESSION_TEACHER_MODAL}-original-teacher`}
              >
                Original teacher: {originalTeacherDisplayName}
              </Badge>
            </div>

            <FormCheckbox
              form={form}
              name="shouldUpdateOriginalTeacher"
              label="Update original teacher?"
              labelClassName="font-normal"
              checkboxProps={{ 'data-testid': `${EDIT_SESSION_TEACHER_MODAL}-checkbox-update-original-teacher` }}
            />

            {shouldUpdateOriginalTeacher ? (
              <p className="text-sm text-muted-foreground">
                ⚠️ Are you sure you want to change the original teacher for this session to be the same as the new
                session teacher?
              </p>
            ) : null}

            <FormComboBox
              form={form}
              name="sessionTeacher"
              label=""
              placeholder="Select a teacher"
              triggerProps={{
                className: 'w-full',
                'data-testid': `${EDIT_SESSION_TEACHER_MODAL}-combobox-session-teacher`,
              }}
              {...comboBoxOptionsOf(coachTypeahead)}
            />

            {!shouldUpdateOriginalTeacher && assignmentState === 'original_teacher' ? (
              <>
                <FormSingleSelect
                  form={form}
                  name="originalTeacherClassIssue"
                  label="Original teacher class issue"
                  options={orderedOriginalTeacherIssueOptions}
                  renderOption={(issue) => {
                    const optionHint = getOriginalTeacherIssueOptionHint(issue);
                    return (
                      <div>
                        <div>{getOriginalTeacherIssueDropdownText(issue)}</div>
                        {optionHint ? <div className="text-muted-foreground">{optionHint}</div> : null}
                      </div>
                    );
                  }}
                  renderOptionSelected={getOriginalTeacherIssueDropdownText}
                  optionClassName={getOriginalTeacherIssueOptionClassName}
                  triggerProps={{ className: 'w-full [&>span]:block [&>span]:text-left' }}
                  triggerTestId={`${EDIT_SESSION_TEACHER_MODAL}-select-original-teacher-issue`}
                />
                {showOriginalTeacherIssueReason ? (
                  <FormTextArea
                    form={form}
                    name="reason"
                    label="Reason"
                    textAreaProps={{
                      className: 'w-full resize-none max-h-24',
                      placeholder: 'Please enter a reason',
                      rows: 3,
                      'data-testid': `${EDIT_SESSION_TEACHER_MODAL}-textarea-reason`,
                    }}
                  />
                ) : null}
              </>
            ) : null}

            {showConfirmationEmail ? (
              <FormCheckbox
                form={form}
                name="shouldSendConfirmationEmail"
                label="Send confirmation email."
                checkboxProps={{ 'data-testid': `${EDIT_SESSION_TEACHER_MODAL}-checkbox-send-confirmation-email` }}
              />
            ) : null}
          </div>
        );
      }}
    </FormModal>
  );
}

export function SessionTeacherWithEditTrigger({
  session,
  teacherTestId,
  triggerTestId,
  showSessionTeacherName = true,
  compactIcon = false,
}: {
  session: AdminSession;
  teacherTestId?: string;
  triggerTestId?: string;
  showSessionTeacherName?: boolean;
  compactIcon?: boolean;
}) {
  const showEditTrigger = canEditSessionTeacher(session);

  if (!showSessionTeacherName && !showEditTrigger) {
    return null;
  }

  return (
    <span className="inline-flex items-center gap-1">
      {showSessionTeacherName ? <SessionTeacherDisplay session={session} testId={teacherTestId} /> : null}
      {showEditTrigger ? (
        <EditSessionTeacherModal session={session} triggerTestId={triggerTestId} compactIcon={compactIcon} />
      ) : null}
    </span>
  );
}
